/**
 * Compete — Sharp8's retention layer (brief §5.2).
 *
 * MVP composition:
 *   • Sharp8 engine record card (top — establishes the public-ledger
 *     credibility before showing user leaderboards)
 *   • Period filter pills: ALL TIME · THIS WEEK · TODAY (rolling 7d /
 *     SGT-today; brief §4.1 anchors timezone to UTC+8)
 *   • Hot Board: Triple Hit (3+ wins today) + Biggest Climbers (top
 *     units-gainers in last 7d), shown above the main leaderboard
 *     when either list has entries
 *   • Podium for ranks 1-3 (avatars + names + units, rank 1 tallest)
 *   • Scrolling list for ranks 4+ (compact row layout)
 *   • Sticky "Your rank" bar at the bottom showing the user's own row
 *     even when they're outside the top 100
 *
 * Deferred from MVP: tap-to-profile, contest prize tile.
 */

import { useEffect, useState } from 'react';
import type { User } from '@sharp8/types';
import { tg } from '../lib/telegram';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface Props {
  me: { user: User; guest: boolean } | null;
}

interface LeaderRow {
  rank: number;
  userId: string;
  username: string | null;
  photoUrl: string | null;
  /** Current game balance — starts at $1,000 per the F2P bankroll model. */
  bankroll: number;
  totalPicks: number;
  settledPicks: number;
  wins: number;
  losses: number;
  winRate: number | null;
  /** Net P&L in $ from settled picks within the selected period. Ranking
   *  metric — period-scoped so week/today filters mean something. */
  profitDollars: number;
  totalStake: number;
}

interface LeaderboardResponse {
  period: 'all' | 'week' | 'today';
  rows: LeaderRow[];
  me: LeaderRow | null;
}

type Period = 'all' | 'week' | 'today';

interface HotBoardRow {
  userId: string;
  username: string | null;
  photoUrl: string | null;
  value: number;
  valueLabel: string;
}

interface HotBoardResponse {
  tripleHit: HotBoardRow[];
  climbers: HotBoardRow[];
}

interface Sharp8Window {
  settled: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number | null;
  units1u: number;
}

interface Sharp8StatsResponse {
  all: Sharp8Window;
  week: Sharp8Window;
}

async function fetchLeaderboard(period: Period): Promise<LeaderboardResponse> {
  const initData = tg()?.initData ?? '';
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/leaderboard?limit=100&period=${period}`, {
    headers: initData ? { 'X-Telegram-Init-Data': initData } : {},
  });
  if (!res.ok) throw new Error(`/leaderboard ${res.status}`);
  return res.json();
}

async function fetchHotBoard(): Promise<HotBoardResponse> {
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/hot-board`);
  if (!res.ok) throw new Error(`/hot-board ${res.status}`);
  return res.json();
}

async function fetchSharp8Stats(): Promise<Sharp8StatsResponse> {
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/stats/sharp8`);
  if (!res.ok) throw new Error(`/stats/sharp8 ${res.status}`);
  return res.json();
}

export function Compete({ me }: Props) {
  const [period, setPeriod] = useState<Period>('all');
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [hot, setHot] = useState<HotBoardResponse | null>(null);
  const [stats, setStats] = useState<Sharp8StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // Note: we don't clear `data` on period switch — the previous
    // period's leaderboard stays visible until the new one arrives,
    // so the pills don't flash unmount/remount during fetch.
    fetchLeaderboard(period)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [period]);

  // Hot Board is period-independent (Triple Hit = today; Climbers = 7d).
  // Fetch once on mount; soft-fail to null so the section just hides.
  useEffect(() => {
    let cancelled = false;
    fetchHotBoard()
      .then((d) => { if (!cancelled) setHot(d); })
      .catch(() => { /* hot board is optional — silent failure */ });
    return () => { cancelled = true; };
  }, []);

  // Sharp8 engine record. Mount-only fetch — recomputed via the
  // cron's settle cycle, no need to refresh as the user navigates.
  // Soft-fail to null so the tile just hides if the endpoint errors.
  useEffect(() => {
    let cancelled = false;
    fetchSharp8Stats()
      .then((d) => { if (!cancelled) setStats(d); })
      .catch(() => { /* engine-record tile is optional — silent failure */ });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="compete-error">
        <p>Couldn't load leaderboard: {error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="compete-loading">Loading leaderboard…</div>;
  }

  const top3 = data.rows.slice(0, 3);
  const rest = data.rows.slice(3);
  const totalRows = data.rows.length;

  return (
    <div className="compete">
      {/* HEAD */}
      <section className="compete__head">
        <div className="compete__eyebrow">Sharp8 · The BoShen Leaderboard</div>
        <h1 className="compete__title">The sharps</h1>
        <p className="compete__sub">
          {period === 'today' ? 'Ranked by net $ won today (SGT). ' :
           period === 'week'  ? 'Ranked by net $ won in the last 7 days. ' :
                                'Ranked by lifetime $ won. '}
          <i>Everyone starts with $1,000 game-dollars.</i>
        </p>
      </section>

      {/* SHARP8 ENGINE RECORD — public-ledger tile establishing
          credibility before showing user leaderboards. Hidden until
          the engine has at least one settled call. */}
      {stats && stats.all.settled > 0 && <Sharp8RecordCard stats={stats} />}

      {/* PERIOD FILTER — settled_at-based window, SGT for 'today' */}
      <section className="compete__period">
        {(['all', 'week', 'today'] as Period[]).map((p) => (
          <button
            key={p}
            type="button"
            className={`compete__period-pill${period === p ? ' is-active' : ''}`}
            onClick={() => setPeriod(p)}
            aria-pressed={period === p}
          >
            {p === 'all' ? 'All time' : p === 'week' ? 'This week' : 'Today'}
          </button>
        ))}
      </section>

      {/* HOT BOARD — Triple Hit + Climbers. Renders only when at least
          one of the two lists has entries (low-activity early days
          shouldn't show empty headers). */}
      {hot && (hot.tripleHit.length > 0 || hot.climbers.length > 0) && (
        <section className="compete__hotboard">
          {hot.tripleHit.length > 0 && (
            <HotBoardList
              title="Triple Hit"
              hint="3+ wins settled today"
              rows={hot.tripleHit}
            />
          )}
          {hot.climbers.length > 0 && (
            <HotBoardList
              title="Biggest Climbers"
              hint="Units gained · last 7 days"
              rows={hot.climbers}
            />
          )}
        </section>
      )}

      {totalRows === 0 ? (
        <EmptyLeaderboard period={period} />
      ) : (
        <>
          {/* PODIUM */}
          <Podium top3={top3} />

          {/* LIST OF RANK 4+ */}
          {rest.length > 0 && (
            <section className="compete__list">
              <div className="compete__list-head">Rank 4+</div>
              <ul>
                {rest.map((r) => (
                  <Row key={r.userId} row={r} isMe={me?.user.id === r.userId} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* STICKY "Your rank" bar — only shows if user is logged in AND
          has at least one pick (data.me will be non-null then). */}
      {data.me && (
        <div className="compete__yourank">
          <div className="compete__yourank-inner">
            <span className="compete__yourank-pos">#{data.me.rank}</span>
            <span className="compete__yourank-label">Your rank</span>
            <span className="compete__yourank-units">
              {data.me.profitDollars >= 0 ? '+' : '−'}${Math.abs(data.me.profitDollars).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Sharp8RecordCard({ stats }: { stats: Sharp8StatsResponse }) {
  return (
    <section className="compete__engine">
      <div className="compete__engine-head">
        <span className="compete__engine-title">Sharp8 · Engine record</span>
        <span className="compete__engine-hint">Public ledger · settled edge calls only</span>
      </div>
      <div className="compete__engine-rows">
        <EngineRow label="Last 7 days" w={stats.week} />
        <EngineRow label="All time"    w={stats.all}  />
      </div>
    </section>
  );
}

function EngineRow({ label, w }: { label: string; w: Sharp8Window }) {
  if (w.settled === 0) {
    return (
      <div className="compete__engine-row">
        <span className="compete__engine-row-label">{label}</span>
        <span className="compete__engine-row-empty">No settled calls yet</span>
      </div>
    );
  }
  const wlText = `${w.wins}W · ${w.losses}L${w.pushes > 0 ? ` · ${w.pushes}P` : ''}`;
  const rateText = w.winRate !== null ? `${(w.winRate * 100).toFixed(0)}% W` : '—';
  const unitsPositive = w.units1u >= 0;
  return (
    <div className="compete__engine-row">
      <span className="compete__engine-row-label">{label}</span>
      <span className="compete__engine-row-record">{wlText}</span>
      <span className="compete__engine-row-rate">{rateText}</span>
      <span className={`compete__engine-row-units${unitsPositive ? ' green' : ' red'}`}>
        {unitsPositive ? '+' : ''}{w.units1u.toFixed(2)}u
      </span>
    </div>
  );
}

function HotBoardList({ title, hint, rows }: { title: string; hint: string; rows: HotBoardRow[] }) {
  return (
    <div className="compete__hotboard-list">
      <div className="compete__hotboard-head">
        <span className="compete__hotboard-title">{title}</span>
        <span className="compete__hotboard-hint">{hint}</span>
      </div>
      <ul>
        {rows.map((r) => (
          <li key={r.userId} className="compete__hotboard-row">
            {r.photoUrl ? (
              <img className="compete__hotboard-avatar" src={r.photoUrl} alt="" />
            ) : (
              <div className="compete__hotboard-avatar compete__hotboard-avatar--fallback">
                {(r.username ?? 'U')[0]?.toUpperCase() ?? 'U'}
              </div>
            )}
            <span className="compete__hotboard-name">{r.username ?? 'Anonymous'}</span>
            <span className="compete__hotboard-value">{r.valueLabel}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Podium({ top3 }: { top3: LeaderRow[] }) {
  const [first, second, third] = top3;

  return (
    <section className="compete__podium">
      {/* Second place (left) */}
      {second ? <PodiumStep row={second} place={2} /> : <div className="compete__podium-empty" />}

      {/* First place (center, tallest) */}
      {first ? <PodiumStep row={first} place={1} /> : <div className="compete__podium-empty" />}

      {/* Third place (right) */}
      {third ? <PodiumStep row={third} place={3} /> : <div className="compete__podium-empty" />}
    </section>
  );
}

function PodiumStep({ row, place }: { row: LeaderRow; place: 1 | 2 | 3 }) {
  return (
    <div className={`compete__podium-step compete__podium-step--${place}`}>
      <div className="compete__podium-medal">{place === 1 ? '★' : place}</div>
      {row.photoUrl ? (
        <img className="compete__podium-avatar" src={row.photoUrl} alt={row.username ?? ''} />
      ) : (
        <div className="compete__podium-avatar compete__podium-avatar--fallback">
          {(row.username ?? 'U')[0]?.toUpperCase() ?? 'U'}
        </div>
      )}
      <div className="compete__podium-name">{row.username ?? 'Anonymous'}</div>
      <div className={`compete__podium-units${row.profitDollars >= 0 ? ' green' : ' red'}`}>
        {row.profitDollars >= 0 ? '+' : '−'}${Math.abs(row.profitDollars).toFixed(2)}
      </div>
      <div className="compete__podium-meta">
        {row.totalPicks} picks · {row.winRate !== null ? `${(row.winRate * 100).toFixed(0)}% W` : '—'}
      </div>
    </div>
  );
}

function Row({ row, isMe }: { row: LeaderRow; isMe: boolean }) {
  return (
    <li className={`compete__row${isMe ? ' is-me' : ''}`}>
      <span className="compete__row-rank">{row.rank}</span>
      {row.photoUrl ? (
        <img className="compete__row-avatar" src={row.photoUrl} alt="" />
      ) : (
        <div className="compete__row-avatar compete__row-avatar--fallback">
          {(row.username ?? 'U')[0]?.toUpperCase() ?? 'U'}
        </div>
      )}
      <div className="compete__row-mid">
        <div className="compete__row-name">{row.username ?? 'Anonymous'}</div>
        <div className="compete__row-meta">{row.totalPicks} picks · {row.winRate !== null ? `${(row.winRate * 100).toFixed(0)}% W` : '—'}</div>
      </div>
      <div className={`compete__row-units${row.profitDollars >= 0 ? ' green' : ' red'}`}>
        {row.profitDollars >= 0 ? '+' : '−'}${Math.abs(row.profitDollars).toFixed(2)}
      </div>
    </li>
  );
}

function EmptyLeaderboard({ period }: { period: Period }) {
  const title =
    period === 'today' ? 'No settled picks today yet' :
    period === 'week'  ? 'No settled picks in the last 7 days' :
                         'First leaderboard rolls out once a match settles';
  const body =
    period === 'today' ? 'Pick a match today — once it settles, your row joins the board.' :
    period === 'week'  ? 'The weekly window is rolling. Once any pick settles in this window, the board fills up.' :
                         'Place picks today. When the matches end, the cron settles every bet and the leaderboard fills up. The top sharp wins.';
  return (
    <div className="compete__empty">
      <div className="compete__empty-icon">
        <svg viewBox="0 0 48 48" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6h24v8a8 8 0 01-8 8h-8a8 8 0 01-8-8V6z" />
          <path d="M12 10H6v4a6 6 0 006 6M36 10h6v4a6 6 0 01-6 6" />
          <path d="M16 42h16M24 22v20" />
        </svg>
      </div>
      <div className="compete__empty-title">{title}</div>
      <div className="compete__empty-body">{body}</div>
    </div>
  );
}
